import React, { useEffect, useRef } from "react";

// TopLoader
// Props:
// - isOn (boolean)         : whether the loader is visible/active
// - percentage (number)    : 0-100 - how far the progress bar is
// - height (number)        : px height of the bar (optional, default 3)
// - className (string)     : optional extra classes for container

export default function TopLoader({ isOn = false, percentage = 0, height = 3, className = "" }) {
  const barRef = useRef(null);
  const wrapperRef = useRef(null);

  // clamp percentage
  const pct = Math.max(0, Math.min(100, Number(percentage) || 0));

  // set initial styles on mount so it's visible even without Tailwind
  useEffect(() => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    // ensure wrapper is positioned and hidden/shown via opacity
    wrap.style.position = "fixed";
    wrap.style.top = "0";
    wrap.style.left = "0";
    wrap.style.right = "0";
    wrap.style.zIndex = "9999";
    wrap.style.pointerEvents = "none";
    wrap.style.height = `${height}px`;
    wrap.style.opacity = isOn ? "1" : "0";
    wrap.style.transition = "opacity 300ms ease";
    wrap.style.display = "block";

    // make wrapper a containing block for absolute child
    wrap.style.boxSizing = "border-box";
  }, [height, isOn]);

  // update width when percentage changes
  useEffect(() => {
    const el = barRef.current;
    if (!el) return;
    // ensure bar has transition if not using Tailwind
    el.style.transition = "width 300ms ease-out, opacity 200ms";
    el.style.transformOrigin = "left";
    el.style.height = "100%";

    // set width based on pct and isOn
    el.style.width = `${isOn ? pct : 0}%`;
    el.style.opacity = isOn ? "1" : "0";

    // when percentage hits 100, hide after a short delay (like YouTube)
    if (pct >= 100) {
      setTimeout(() => {
        if (wrapperRef.current) wrapperRef.current.style.opacity = "0";
      }, 300);
    } else if (isOn) {
      if (wrapperRef.current) wrapperRef.current.style.opacity = "1";
    }
  }, [pct, isOn]);

  // show/hide when isOn toggles (keeps fallback behavior)
  useEffect(() => {
    const wrap = wrapperRef.current;
    if (!wrap) return;
    if (isOn) {
      wrap.style.opacity = "1";
    } else {
      wrap.style.opacity = "0";
      const t = setTimeout(() => {
        if (barRef.current) barRef.current.style.width = "0%";
      }, 300);
      return () => clearTimeout(t);
    }
  }, [isOn]);

  return (
    <div
      ref={wrapperRef}
      aria-hidden={!isOn}
      className={`top-loader-wrapper ${className}`}
    >
      {/* progress bar */}
      <div
        ref={barRef}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        className="top-loader-bar"
        style={{
          width: isOn ? `${pct}%` : "0%",
          background: "linear-gradient(90deg,#ff0000,#ff4d4d)",
          boxShadow: "0 2px 8px rgba(255,77,77,0.35)",
          transformOrigin: "left",
        }}
      />

      {/* thin separator line below */}
      <div
        style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 1, pointerEvents: "none", background: "rgba(0,0,0,0.06)" }}
      />
    </div>
  );
}
